import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  GearSix, Plus, Trash, PencilSimple, ArrowLeft, Eye, EyeSlash,
  Check, X, Users, ChartBar, Trophy, Clock, FileText, BookOpen,
  SpeakerHigh, ArrowsDownUp, Download, LockOpen, ShieldWarning,
  CaretDown, CaretRight
} from "@phosphor-icons/react";

const ROLES = [
  { value: "opportunity_finder", label: "Opportunity Finder" },
  { value: "senior_recruitment_partner", label: "Senior Recruitment Partner" },
  { value: "candidate_finder", label: "Candidate Finder" },
  { value: "team_leader", label: "Team Leader" },
  { value: "admin", label: "Admin" },
];

export default function AcademyAdminPage() {
  const { user } = useAuth();
  const [view, setView] = useState("list");
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [editLesson, setEditLesson] = useState(null);
  const [editQuestion, setEditQuestion] = useState(null);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQ, setNewQ] = useState({ type: "multiple_choice", question: "", options: ["", "", "", ""], correct_answer: "", explanation: "", points: 1 });
  const [userProgress, setUserProgress] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => { loadModules(); }, []);

  const loadModules = async () => {
    try {
      const { data } = await api.get("/academy/admin/modules");
      setModules(data.modules || []);
    } catch { toast.error("Failed to load modules"); }
    finally { setLoading(false); }
  };

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get("/academy/analytics");
      setAnalytics(data);
      setView("analytics");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const loadUserProgress = async () => {
    try {
      const { data } = await api.get("/academy/admin/user-progress");
      setUserProgress(data.items || []);
      setView("progress");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load user progress"); }
  };

  const resetAttempts = async (userId, moduleId, userName, moduleTitle) => {
    try {
      await api.post("/academy/admin/reset-attempts", { user_id: userId, module_id: moduleId });
      toast.success(`Reset attempts for ${userName} on ${moduleTitle}`);
      loadUserProgress();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reset"); }
  };

  const createModule = async () => {
    try {
      const { data } = await api.post("/academy/admin/modules", { title: "New Module", description: "", status: "draft" });
      toast.success("Module created");
      loadModules();
      openModule(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openModule = async (id) => {
    try {
      const m = modules.find(x => x.id === id);
      if (m) { setSelected(m); setView("edit"); return; }
      const { data } = await api.get(`/academy/modules/${id}`);
      setSelected(data);
      setView("edit");
    } catch { toast.error("Failed"); }
  };

  const updateModule = async (field, value) => {
    if (!selected) return;
    const updated = { ...selected, [field]: value };
    setSelected(updated);
    try {
      await api.put(`/academy/admin/modules/${selected.id}`, { [field]: value });
    } catch { toast.error("Failed to save"); }
  };

  const deleteModule = async (id) => {
    if (!window.confirm("Delete this module and all progress?")) return;
    try {
      await api.delete(`/academy/admin/modules/${id}`);
      toast.success("Deleted");
      setView("list");
      setSelected(null);
      loadModules();
    } catch { toast.error("Failed"); }
  };

  const addLesson = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/academy/admin/modules/${selected.id}/lessons`, { title: "New Lesson" });
      const updated = { ...selected, lessons: [...(selected.lessons || []), data] };
      setSelected(updated);
      setEditLesson(data);
      toast.success("Lesson added");
    } catch { toast.error("Failed"); }
  };

  const saveLesson = async () => {
    if (!editLesson || !selected) return;
    try {
      await api.put(`/academy/admin/lessons/${selected.id}/${editLesson.id}`, editLesson);
      const lessons = (selected.lessons || []).map(l => l.id === editLesson.id ? editLesson : l);
      setSelected({ ...selected, lessons });
      setEditLesson(null);
      toast.success("Lesson saved");
    } catch { toast.error("Failed"); }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await api.delete(`/academy/admin/lessons/${selected.id}/${lessonId}`);
      const lessons = (selected.lessons || []).filter(l => l.id !== lessonId);
      setSelected({ ...selected, lessons });
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const addQuestion = async () => {
    const q = { ...newQ, id: crypto.randomUUID() };
    const questions = [...(selected.questions || []), q];
    await updateModule("questions", questions);
    setShowAddQuestion(false);
    setNewQ({ type: "multiple_choice", question: "", options: ["", "", "", ""], correct_answer: "", explanation: "", points: 1 });
  };

  const removeQuestion = async (qId) => {
    const questions = (selected.questions || []).filter(q => q.id !== qId);
    await updateModule("questions", questions);
  };

  const downloadCertPdf = async (certId) => {
    try {
      const res = await api.get(`/academy/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `cert-${certId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Failed"); }
  };

  if (user?.role !== "admin") return <div className="p-8 text-center text-[#6B7280]">Admin access required</div>;

  // ============ USER PROGRESS ============
  if (view === "progress") {
    const lockedCount = userProgress.filter(u => u.locked_count > 0).length;
    return (
      <div data-testid="academy-user-progress" className="space-y-4">
        <button onClick={() => setView("list")} className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#0F172A]">User Academy Progress</h1>
            <p className="text-sm text-[#6B7280] mt-0.5">{userProgress.length} users · {lockedCount > 0 ? <span className="text-red-600 font-semibold">{lockedCount} locked</span> : "0 locked"}</p>
          </div>
        </div>

        {/* Locked users banner */}
        {lockedCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="locked-users-banner">
            <ShieldWarning size={24} className="text-red-500 shrink-0 mt-0.5" weight="fill" />
            <div>
              <p className="text-sm font-bold text-red-800">{lockedCount} user{lockedCount > 1 ? "s" : ""} locked out of academy assessments</p>
              <p className="text-xs text-red-600 mt-0.5">These users have exceeded the maximum attempts and cannot continue. Use the Reset button below to unlock them.</p>
            </div>
          </div>
        )}

        {/* Users list */}
        <div className="space-y-3">
          {userProgress.map(up => {
            const isExpanded = expandedUser === up.user_id;
            const hasLocked = up.locked_count > 0;
            return (
              <div key={up.user_id} className={`bg-white rounded-xl border ${hasLocked ? "border-red-200" : "border-[#E5E7EB]"} overflow-hidden`}>
                {/* User header */}
                <button
                  className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#F9FAFB] transition"
                  onClick={() => setExpandedUser(isExpanded ? null : up.user_id)}
                  data-testid={`user-progress-${up.user_id}`}
                >
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${hasLocked ? "bg-red-500" : "bg-gradient-to-br from-[#0F172A] to-[#334155]"}`}>
                      {up.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    {hasLocked && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">!</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-[#0F172A]">{up.name}</p>
                      {hasLocked && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">LOCKED</span>}
                    </div>
                    <p className="text-xs text-[#6B7280]">{up.email} · {({opportunity_finder:"RP",candidate_finder:"CF",team_leader:"TL",senior_recruitment_partner:"SRP"})[up.role] || up.role}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {/* Progress bar */}
                    <div className="w-32 hidden sm:block">
                      <div className="flex items-center justify-between text-[10px] text-[#6B7280] mb-1">
                        <span>Progress</span>
                        <span className="font-medium">{up.completed}/{up.total_modules}</span>
                      </div>
                      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${hasLocked ? "bg-red-400" : up.completed >= up.total_modules ? "bg-emerald-500" : "bg-[#C5A059]"}`} style={{width: `${up.total_modules > 0 ? (up.completed / up.total_modules) * 100 : 0}%`}} />
                      </div>
                    </div>
                    {isExpanded ? <CaretDown size={16} className="text-[#9CA3AF]" /> : <CaretRight size={16} className="text-[#9CA3AF]" />}
                  </div>
                </button>

                {/* Expanded module details */}
                {isExpanded && (
                  <div className="border-t border-[#F1F5F9] bg-[#FAFAFA]">
                    <div className="px-5 py-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[#6B7280]">
                            <th className="text-left py-2 font-medium">Module</th>
                            <th className="text-center py-2 font-medium">Status</th>
                            <th className="text-center py-2 font-medium">Attempts</th>
                            <th className="text-center py-2 font-medium">Score</th>
                            <th className="text-right py-2 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(up.modules || []).map(mod => {
                            const statusColor = {
                              completed: "bg-emerald-100 text-emerald-800",
                              in_progress: "bg-blue-100 text-blue-800",
                              not_started: "bg-gray-100 text-gray-600",
                            };
                            return (
                              <tr key={mod.module_id} className={`border-t border-[#E5E7EB] ${mod.locked ? "bg-red-50" : ""}`}>
                                <td className="py-2.5 pr-3">
                                  <p className={`font-medium ${mod.locked ? "text-red-700" : "text-[#0F172A]"}`}>{mod.module_title}</p>
                                </td>
                                <td className="text-center py-2.5">
                                  {mod.locked ? (
                                    <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">LOCKED</span>
                                  ) : (
                                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${statusColor[mod.status] || statusColor.not_started}`}>
                                      {(mod.status || "not_started").replace(/_/g, " ")}
                                    </span>
                                  )}
                                </td>
                                <td className="text-center py-2.5">
                                  <span className={mod.locked ? "text-red-600 font-bold" : "text-[#374151]"}>{mod.attempts}/{mod.max_attempts}</span>
                                </td>
                                <td className="text-center py-2.5">
                                  {mod.score > 0 ? (
                                    <span className={mod.passed ? "text-emerald-600 font-medium" : "text-red-600"}>{mod.score}%</span>
                                  ) : <span className="text-[#9CA3AF]">—</span>}
                                </td>
                                <td className="text-right py-2.5">
                                  {(mod.locked || mod.attempts > 0) && (
                                    <button
                                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded transition"
                                      onClick={() => resetAttempts(up.user_id, mod.module_id, up.name, mod.module_title)}
                                      data-testid={`reset-${up.user_id}-${mod.module_id}`}
                                    >
                                      <LockOpen size={12} /> Reset
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {userProgress.length === 0 && (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center text-[#9CA3AF]">No user progress data found</div>
          )}
        </div>
      </div>
    );
  }

  // ============ ANALYTICS ============
  if (view === "analytics" && analytics) {
    return (
      <div data-testid="academy-analytics" className="space-y-4">
        <button onClick={() => setView("list")} className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
        <h1 className="text-xl font-bold text-[#0F172A]">Academy Analytics</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Users", val: analytics.total_users, color: "#0F172A" },
            { label: "Enrolled", val: analytics.users_enrolled, color: "#3B82F6" },
            { label: "Modules Done", val: analytics.modules_completed, color: "#10B981" },
            { label: "Avg Score", val: `${analytics.average_score}%`, color: "#C5A059" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg border p-4">
              <p className="text-[10px] text-[#6B7280] uppercase font-medium">{s.label}</p>
              <p className="text-xl font-bold mt-1" style={{ color: s.color }}>{s.val}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-[10px] text-[#6B7280] uppercase font-medium">Failed Assessments</p>
            <p className="text-xl font-bold text-red-500 mt-1">{analytics.failed_assessments}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-[10px] text-[#6B7280] uppercase font-medium">Total Training Hours</p>
            <p className="text-xl font-bold text-[#0F172A] mt-1">{analytics.total_time_hours}h</p>
          </div>
        </div>
        {analytics.user_breakdown?.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="p-4 border-b"><h3 className="text-sm font-semibold text-[#0F172A]">Trainee Progress</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    <th className="text-left px-4 py-2 text-[10px] text-[#6B7280] uppercase font-medium">Trainee</th>
                    <th className="text-center px-4 py-2 text-[10px] text-[#6B7280] uppercase font-medium">Completed</th>
                    <th className="text-center px-4 py-2 text-[10px] text-[#6B7280] uppercase font-medium">In Progress</th>
                    <th className="text-center px-4 py-2 text-[10px] text-[#6B7280] uppercase font-medium">Avg Score</th>
                    <th className="text-center px-4 py-2 text-[10px] text-[#6B7280] uppercase font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.user_breakdown.map((u, i) => (
                    <tr key={i} className="border-t hover:bg-[#F9FAFB]">
                      <td className="px-4 py-2.5 font-medium text-[#0F172A]">{u.user_name || "Unknown"}</td>
                      <td className="text-center px-4 py-2.5 text-green-600 font-medium">{u.completed}</td>
                      <td className="text-center px-4 py-2.5 text-blue-600">{u.in_progress}</td>
                      <td className="text-center px-4 py-2.5">{u.avg_score}%</td>
                      <td className="text-center px-4 py-2.5 text-[#6B7280]">{Math.round(u.time_spent / 60)}m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ MODULE EDITOR ============
  if (view === "edit" && selected) {
    return (
      <div data-testid="academy-module-editor" className="space-y-4 max-w-4xl">
        <button onClick={() => { setView("list"); setSelected(null); loadModules(); }} className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"><ArrowLeft size={12} /> All Modules</button>

        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#0F172A]">Edit Module</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => updateModule("status", selected.status === "published" ? "draft" : "published")} className="gap-1" data-testid="toggle-publish-btn">
              {selected.status === "published" ? <><EyeSlash size={14} /> Unpublish</> : <><Eye size={14} /> Publish</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => deleteModule(selected.id)} className="gap-1 text-red-500 border-red-300"><Trash size={14} /> Delete</Button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <h3 className="text-xs font-semibold text-[#6B7280] uppercase">Module Details</h3>
          <Input value={selected.title} onChange={e => setSelected({...selected, title: e.target.value})} onBlur={() => updateModule("title", selected.title)} placeholder="Module Title" data-testid="module-title-input" />
          <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} value={selected.description || ""} onChange={e => setSelected({...selected, description: e.target.value})} onBlur={() => updateModule("description", selected.description)} placeholder="Description" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Order</label>
              <Input type="number" value={selected.order || ""} onChange={e => setSelected({...selected, order: parseInt(e.target.value) || 0})} onBlur={() => updateModule("order", selected.order)} />
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Duration (min)</label>
              <Input type="number" value={selected.estimated_duration_minutes || ""} onChange={e => setSelected({...selected, estimated_duration_minutes: parseInt(e.target.value) || 0})} onBlur={() => updateModule("estimated_duration_minutes", selected.estimated_duration_minutes)} />
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Pass Mark (%)</label>
              <Input type="number" value={selected.pass_mark || ""} onChange={e => setSelected({...selected, pass_mark: parseInt(e.target.value) || 0})} onBlur={() => updateModule("pass_mark", selected.pass_mark)} data-testid="pass-mark-input" />
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Max Attempts</label>
              <Input type="number" value={selected.max_attempts || ""} onChange={e => setSelected({...selected, max_attempts: parseInt(e.target.value) || 0})} onBlur={() => updateModule("max_attempts", selected.max_attempts)} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#6B7280] font-medium block mb-1">Roles</label>
            <div className="flex flex-wrap gap-2">
              {ROLES.map(r => (
                <button key={r.value} onClick={() => {
                  const roles = selected.roles || [];
                  const updated = roles.includes(r.value) ? roles.filter(x => x !== r.value) : [...roles, r.value];
                  updateModule("roles", updated);
                }} className={`px-3 py-1 rounded-full text-xs font-medium border transition ${(selected.roles || []).includes(r.value) ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#6B7280] hover:bg-gray-50"}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#6B7280] font-medium block mb-1">Objectives (one per line)</label>
            <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={3} value={(selected.objectives || []).join("\n")} onChange={e => setSelected({...selected, objectives: e.target.value.split("\n")})} onBlur={() => updateModule("objectives", selected.objectives)} />
          </div>
          <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded ${selected.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{selected.status?.toUpperCase()}</span>
          <span className="text-xs text-[#6B7280] ml-2">{selected.enrolled_count || 0} enrolled · {selected.completed_count || 0} completed</span>
        </div>

        {/* Lessons */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-[#6B7280] uppercase">Lessons ({(selected.lessons || []).length})</h3>
            <Button size="sm" variant="outline" onClick={addLesson} className="gap-1" data-testid="add-lesson-btn"><Plus size={12} /> Add Lesson</Button>
          </div>
          {(selected.lessons || []).sort((a, b) => a.order - b.order).map((l, i) => (
            <div key={l.id} className="border rounded-lg p-3">
              {editLesson?.id === l.id ? (
                <div className="space-y-2">
                  <Input value={editLesson.title} onChange={e => setEditLesson({...editLesson, title: e.target.value})} placeholder="Lesson Title" />
                  <Input type="number" value={editLesson.order} onChange={e => setEditLesson({...editLesson, order: parseInt(e.target.value) || 0})} placeholder="Order" />
                  <Input value={editLesson.video_url || ""} onChange={e => setEditLesson({...editLesson, video_url: e.target.value})} placeholder="Video URL (YouTube embed or direct)" />
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={6} value={editLesson.written_content || ""} onChange={e => setEditLesson({...editLesson, written_content: e.target.value})} placeholder="Written Content (use **bold** and - for bullets)" />
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={3} value={editLesson.voiceover_script || ""} onChange={e => setEditLesson({...editLesson, voiceover_script: e.target.value})} placeholder="Voiceover Script (narration text)" />
                  <Input value={editLesson.audio_url || ""} onChange={e => setEditLesson({...editLesson, audio_url: e.target.value})} placeholder="Audio URL (mp3)" />
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} value={(editLesson.key_points || []).join("\n")} onChange={e => setEditLesson({...editLesson, key_points: e.target.value.split("\n").filter(Boolean)})} placeholder="Key Points (one per line)" />
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} value={editLesson.practical_task || ""} onChange={e => setEditLesson({...editLesson, practical_task: e.target.value})} placeholder="Practical Task (optional)" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveLesson} className="bg-[#10B981]" data-testid="save-lesson-btn">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditLesson(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{l.order}. {l.title}</p>
                    <div className="flex gap-2 mt-0.5">
                      {l.video_url && <span className="text-[10px] text-blue-600">Video</span>}
                      {l.voiceover_script && <span className="text-[10px] text-purple-600">Voiceover</span>}
                      {l.practical_task && <span className="text-[10px] text-amber-600">Practical</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditLesson({...l})}><PencilSimple size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteLesson(l.id)} className="text-red-500"><Trash size={14} /></Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Questions */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-[#6B7280] uppercase">Assessment Questions ({(selected.questions || []).length})</h3>
            <Button size="sm" variant="outline" onClick={() => setShowAddQuestion(true)} className="gap-1" data-testid="add-question-btn"><Plus size={12} /> Add Question</Button>
          </div>
          {showAddQuestion && (
            <div className="border rounded-lg p-3 space-y-2 bg-[#F9FAFB]">
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={newQ.type} onChange={e => setNewQ({...newQ, type: e.target.value})}>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">True/False</option>
                <option value="scenario">Scenario</option>
              </select>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} value={newQ.question} onChange={e => setNewQ({...newQ, question: e.target.value})} placeholder="Question text" />
              {newQ.type !== "true_false" && (
                <div className="space-y-1">
                  {newQ.options.map((o, i) => (
                    <Input key={i} value={o} onChange={e => { const opts = [...newQ.options]; opts[i] = e.target.value; setNewQ({...newQ, options: opts}); }} placeholder={`Option ${i + 1}`} />
                  ))}
                </div>
              )}
              <Input value={newQ.correct_answer} onChange={e => setNewQ({...newQ, correct_answer: e.target.value})} placeholder={newQ.type === "true_false" ? "true or false" : "Correct answer (must match exactly)"} />
              <Input value={newQ.explanation} onChange={e => setNewQ({...newQ, explanation: e.target.value})} placeholder="Explanation (shown after answering)" />
              <div className="flex gap-2">
                <Button size="sm" onClick={addQuestion} className="bg-[#10B981]">Add</Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddQuestion(false)}>Cancel</Button>
              </div>
            </div>
          )}
          {(selected.questions || []).map((q, i) => (
            <div key={q.id} className="border rounded-lg p-3 flex items-start justify-between">
              <div>
                <span className="text-[10px] text-[#6B7280] uppercase font-medium">{q.type}</span>
                <p className="text-sm text-[#0F172A]">{q.question}</p>
                <p className="text-xs text-green-600 mt-0.5">Answer: {q.correct_answer}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeQuestion(q.id)} className="text-red-500"><Trash size={14} /></Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ============ MODULE LIST ============
  return (
    <div data-testid="academy-admin" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Academy Administration</h1>
          <p className="text-sm text-[#6B7280]">Manage training modules, lessons, assessments and analytics</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadUserProgress} className="gap-1" data-testid="view-user-progress-btn"><Users size={16} /> User Progress</Button>
          <Button variant="outline" onClick={loadAnalytics} className="gap-1" data-testid="view-analytics-btn"><ChartBar size={16} /> Analytics</Button>
          <Button onClick={createModule} className="bg-[#0F172A] gap-1" data-testid="create-module-btn"><Plus size={16} /> New Module</Button>
        </div>
      </div>

      {loading ? <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />)}</div> : (
        <div className="space-y-2">
          {modules.map((m, i) => (
            <button key={m.id} onClick={() => { setSelected(m); setView("edit"); }} className="w-full bg-white rounded-lg border p-4 text-left hover:shadow-md transition" data-testid={`admin-module-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#0F172A] text-white text-sm font-bold flex items-center justify-center">{m.order}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-[#0F172A]">{m.title}</h3>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${m.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{m.status}</span>
                    </div>
                    <p className="text-xs text-[#6B7280] mt-0.5">{(m.lessons || []).length} lessons · {(m.questions || []).length} questions · {m.enrolled_count || 0} enrolled · {m.completed_count || 0} completed</p>
                  </div>
                </div>
                <PencilSimple size={16} className="text-[#C5A059]" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
